import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Letterhead, type LetterheadCompany } from '@/components/company/Letterhead';
import { useParams } from 'react-router-dom';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { AnswerPanel } from '@/components/publicEstimate/AnswerPanel';
import { DocumentTerms } from '@/components/company/DocumentTerms';
import { formatCurrency, formatDate, formatDateOnly, formatInstantWithZone } from '@/lib/format';
import {
  PublicAnswerRefusedError,
  PublicEstimateGoneError,
  usePublicAnswer,
  usePublicEstimate,
  type PublicEstimate as PublicEstimateData,
  type PublicEstimateDetail,
} from '@/routes/publicEstimate/usePublicEstimate';

/**
 * The customer's estimate, opened from its link with no sign-in (M2-07, pass B).
 *
 * ⚠️ Routed OUTSIDE `RequireAuth` and `AppLayout` (see `App.tsx`), and read
 * through `publicClient`, not `apiClient` — no bearer token, and no 401 ever
 * sends the customer to the office's sign-in page.
 *
 * Mobile-first: most customers open this from a text or an email on a phone,
 * so the lines are a stacked list that reads the same at 375px as on a
 * desktop, never a table that scrolls sideways.
 *
 * <b>Every figure is the API's.</b> Nothing here adds up a line or a total —
 * a document somebody was sent keeps the numbers it was sent with.
 *
 * What the customer sees is exactly the product decision (Kyle, 2026-09-27):
 * who it is for and where, the lines, the totals, the shop's message and how
 * long it is good for. The office's detail (tax categories, notes, ids) has no
 * field on the public response to come from.
 */
export function PublicEstimate() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const estimate = usePublicEstimate(token);
  // Held HERE, above every state the page can turn into: a 409 re-reads the
  // page, which may then be Approved by the office, Expired or Superseded —
  // and the API's sentence must still be on screen when it is (M2-08).
  const answer = usePublicAnswer(token ?? '');
  const refused = [answer.approve.error, answer.decline.error].find(
    (error): error is PublicAnswerRefusedError => error instanceof PublicAnswerRefusedError,
  );
  const notice = refused?.message;
  const answered = answer.approve.isSuccess || answer.decline.isSuccess;

  if (estimate.isPending) {
    return (
      <Page>
        <LoadingState label={t('publicEstimate.loading')} className="justify-center" />
      </Page>
    );
  }

  if (estimate.error instanceof PublicEstimateGoneError) {
    return (
      <Page>
        <Explained title={t('publicEstimate.goneTitle')}>
          {t('publicEstimate.goneDescription')}
        </Explained>
      </Page>
    );
  }

  if (estimate.isError) {
    // A 409's re-read can fail too (the network); its sentence still stands.
    return (
      <Page notice={notice}>
        <ErrorState
          title={t('publicEstimate.loadErrorTitle')}
          description={t('publicEstimate.loadErrorDescription')}
          onRetry={() => void estimate.refetch()}
        />
      </Page>
    );
  }

  return <Loaded estimate={estimate.data} notice={notice} answer={answer} answered={answered} />;
}

function Loaded({
  estimate,
  notice,
  answer,
  answered,
}: {
  estimate: PublicEstimateData;
  notice: string | undefined;
  answer: ReturnType<typeof usePublicAnswer>;
  answered: boolean;
}) {
  const { t } = useTranslation();
  const company = estimate.companyName;
  // M2-19: the shop as it prints itself — on every state, because a customer
  // holding an expired quote is exactly the one who needs the phone number.
  const letterhead: LetterheadCompany = {
    name: company,
    address: estimate.company?.address ?? null,
    phone: estimate.company?.phone ?? null,
    licenseNumber: estimate.company?.licenseNumber ?? null,
    logoPath: estimate.company?.logoPath ?? null,
  };
  const number = Number(estimate.number);
  const revision = Number(estimate.revision);
  const title =
    revision > 1
      ? t('publicEstimate.titleRevision', { number, revision })
      : t('publicEstimate.title', { number });

  useDocumentTitle(t('publicEstimate.documentTitle', { company, title }));

  if (estimate.state === 'Superseded') {
    return (
      <Page company={letterhead} notice={notice}>
        <Explained title={t('publicEstimate.supersededTitle')} eyebrow={title}>
          {t('publicEstimate.supersededDescription', { company })}
        </Explained>
      </Page>
    );
  }

  if (estimate.state === 'Expired') {
    return (
      <Page company={letterhead} notice={notice}>
        <Explained title={t('publicEstimate.expiredTitle')} eyebrow={title}>
          {estimate.expiresOn
            ? t('publicEstimate.expiredOn', { date: formatDateOnly(estimate.expiresOn), company })
            : t('publicEstimate.expired', { company })}
        </Explained>
      </Page>
    );
  }

  // Open, Approved and Declined carry the quote; anything else — a state a
  // later API adds before this page learns it — is shown as no longer valid
  // rather than guessed at.
  if (estimate.estimate === null || !READABLE.has(estimate.state)) {
    return (
      <Page company={letterhead} notice={notice}>
        <Explained title={t('publicEstimate.goneTitle')}>
          {t('publicEstimate.goneDescription')}
        </Explained>
      </Page>
    );
  }

  return (
    <Page company={letterhead} notice={notice}>
      <Quote
        title={title}
        state={estimate.state}
        company={company}
        expiresOn={estimate.expiresOn}
        detail={estimate.estimate}
        consent={notice ? null : estimate.consent}
        answer={answer}
        answered={answered}
      />
    </Page>
  );
}

const READABLE = new Set(['Open', 'Approved', 'Declined']);

function Quote({
  title,
  state,
  company,
  expiresOn,
  detail,
  consent,
  answer,
  answered,
}: {
  title: string;
  state: string;
  company: string;
  expiresOn: string | null;
  detail: PublicEstimateDetail;
  /** The consent the API gives an Open link — null hides the answer (none given, or a 409 on screen). */
  consent: PublicEstimateData['consent'];
  answer: ReturnType<typeof usePublicAnswer>;
  /** The customer answered in this visit: the outcome takes focus and thanks them. */
  answered: boolean;
}) {
  const { t } = useTranslation();
  const address = detail.serviceAddress;
  // On the VIEWER's clock, deliberately: the response carries no shop zone,
  // and the customer reading their own record is the person whose day it is.
  const decidedOn = detail.decidedAt ? formatDate(detail.decidedAt, { dateStyle: 'medium' }) : '';
  // The signature's date AND time, on that same viewer's clock, from ONE
  // formatter — and the zone named, because a time is only evidence with its
  // clock beside it (M2-08).
  const signature = detail.signature;
  const signedAt = signature ? formatInstantWithZone(signature.signedAt) : '';
  const outcome = useRef<HTMLDivElement>(null);
  const answeredState = state === 'Approved' || state === 'Declined';
  useEffect(() => {
    if (answered && answeredState) outcome.current?.focus();
  }, [answered, answeredState]);

  return (
    <article aria-labelledby="public-estimate-title" className="space-y-6">
      <div className="space-y-1">
        <h1 id="public-estimate-title" className="text-2xl font-semibold tracking-tight">
          {title}
        </h1>
        {state === 'Open' && expiresOn ? (
          <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="valid-through">
            {t('publicEstimate.validThrough', { date: formatDateOnly(expiresOn) })}
          </p>
        ) : null}
      </div>

      {answeredState ? (
        <div
          ref={outcome}
          tabIndex={-1}
          className="space-y-1 rounded-md border-l-4 border-[var(--color-primary)] bg-[var(--color-muted)] px-4 py-3 focus:outline-none"
        >
          <p className="font-medium" data-testid="outcome">
            {state === 'Approved'
              ? t('publicEstimate.approvedOn', { date: decidedOn })
              : t('publicEstimate.declinedOn', { date: decidedOn })}
          </p>
          {state === 'Approved' && signature ? (
            <p className="text-sm" data-testid="signed-by">
              {t('publicEstimate.signedBy', { name: signature.signerName, date: signedAt })}
            </p>
          ) : null}
          {answered ? (
            <p className="text-sm" data-testid="thanks">
              {t('publicEstimate.answer.thanks', { company })}
            </p>
          ) : null}
        </div>
      ) : null}

      <section aria-labelledby="public-estimate-for" className="space-y-1">
        <h2
          id="public-estimate-for"
          className="text-xs font-medium tracking-wide text-[var(--color-muted-foreground)] uppercase"
        >
          {t('publicEstimate.preparedFor')}
        </h2>
        <p className="font-medium" data-testid="prepared-for">
          {detail.customerName}
        </p>
        <address className="text-sm not-italic" data-testid="service-address">
          {address.line1}
          <br />
          {address.line2 ? (
            <>
              {address.line2}
              <br />
            </>
          ) : null}
          {t('publicEstimate.cityLine', {
            city: address.city,
            state: address.state,
            postalCode: address.postalCode,
          })}
        </address>
      </section>

      <section aria-labelledby="public-estimate-lines" className="space-y-2">
        <h2 id="public-estimate-lines" className="text-base font-semibold">
          {t('publicEstimate.linesHeading')}
        </h2>
        <ul className="divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
          {detail.lines.map((line, index) => (
            <li
              // Lines carry no id on the public response, by design; they are
              // never reordered here, so the position is a stable key.
              key={index}
              className="flex items-start justify-between gap-4 py-3"
              data-testid="public-line"
            >
              <div className="min-w-0 space-y-0.5">
                <p className="break-words">{line.description}</p>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {t('publicEstimate.qtyAtPrice', {
                    quantity: Number(line.quantity),
                    price: formatCurrency(Number(line.unitPrice)),
                  })}
                </p>
                {Number(line.discount) > 0 ? (
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    {t('publicEstimate.discount', {
                      amount: formatCurrency(Number(line.discount)),
                    })}
                  </p>
                ) : null}
              </div>
              <p className="shrink-0 text-right font-medium tabular-nums">
                {formatCurrency(Number(line.total))}
              </p>
            </li>
          ))}
        </ul>
        <dl
          className="ml-auto grid max-w-xs grid-cols-[1fr_auto] gap-x-6 gap-y-1 pt-2 text-sm"
          data-testid="public-totals"
        >
          <dt>{t('publicEstimate.subtotal')}</dt>
          <dd className="text-right tabular-nums" data-testid="public-subtotal">
            {formatCurrency(Number(detail.subtotal))}
          </dd>
          <dt>{t('publicEstimate.tax')}</dt>
          <dd className="text-right tabular-nums" data-testid="public-tax">
            {formatCurrency(Number(detail.tax))}
          </dd>
          <dt className="text-base font-semibold">{t('publicEstimate.total')}</dt>
          <dd
            className="text-right text-base font-semibold tabular-nums"
            data-testid="public-total"
          >
            {formatCurrency(Number(detail.total))}
          </dd>
        </dl>
      </section>

      {detail.message ? (
        <section aria-labelledby="public-estimate-message" className="space-y-1">
          <h2 id="public-estimate-message" className="text-base font-semibold">
            {t('publicEstimate.messageHeading', { company })}
          </h2>
          <p className="text-sm whitespace-pre-line" data-testid="public-message">
            {detail.message}
          </p>
        </section>
      ) : null}

      {/* M2-48: the terms and warranty this revision went out with — above the
          answer, whose consent line (v2, from the API) says "the terms above". */}
      <DocumentTerms
        terms={detail.terms?.terms}
        warrantyStatement={detail.terms?.warrantyStatement}
        termsHeading={t('documentTerms.estimateTerms')}
      />

      {/* The customer's own answer (M2-08), while the link is Open. */}
      {state === 'Open' && consent ? <AnswerPanel consent={consent} answer={answer} /> : null}
    </article>
  );
}

/**
 * The page's frame: the shop's name as the header, then the content in one
 * readable column. ⚠️ The header is a row so a logo can sit before the name
 * when M2-19 / M2-40 give shops one; today the name is all there is.
 */
function Page({
  company,
  notice,
  children,
}: {
  company?: LetterheadCompany;
  /** The API's sentence for an answer it refused (409) — shown whatever the page now is. */
  notice?: string;
  children: ReactNode;
}) {
  // The form the customer was on is gone when a 409 lands (and focus with
  // it, to the body): put them on the sentence that explains why.
  const noticeRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (notice) noticeRef.current?.focus();
  }, [notice]);
  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {company ? (
        <header className="border-b border-[var(--color-border)]">
          <div className="mx-auto max-w-2xl px-4 py-4">
            <Letterhead company={company} />
          </div>
        </header>
      ) : null}
      <main className="mx-auto max-w-2xl space-y-6 px-4 py-6">
        {notice ? (
          <p
            ref={noticeRef}
            role="alert"
            tabIndex={-1}
            className="rounded-md border border-[var(--color-destructive)] px-4 py-3 text-sm focus:outline-none"
            data-testid="answer-refused"
          >
            {notice}
          </p>
        ) : null}
        {children}
      </main>
    </div>
  );
}

/** A link with no price to show: one heading and the sentence that explains it. */
function Explained({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby="public-estimate-explained" className="space-y-3 pt-6 text-center">
      {eyebrow ? <p className="text-sm text-[var(--color-muted-foreground)]">{eyebrow}</p> : null}
      <h1 id="public-estimate-explained" className="text-xl font-semibold tracking-tight">
        {title}
      </h1>
      <p className="mx-auto max-w-prose text-sm text-[var(--color-muted-foreground)]">{children}</p>
    </section>
  );
}

/** The tab's title while the page is open, so the customer can find it among others. */
function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
